import React, { useState, useRef, useEffect, useCallback } from 'react';

import { PRESETS, DEFAULT_SWATCHES } from '../../../lib/themeTokens';

/** Tokens shown as dots on a preset row -- enough to read the palette. */
const CHIP_KEYS = ['themeAccent', 'themeTextStrong', 'themeText', 'themeTextMuted', 'themeBorder'];

/**
 * The preset selector: one dropdown at the head of the rail.
 *
 * Shipped as a row of chips, which was right for six presets and a nine-token
 * set -- they fitted on one line and the whole theme WAS the palette they
 * showed. At 48 tokens the row is the widest thing in a 34rem rail and still
 * only describes a fifth of what a preset writes, so it costs a lot of the
 * one dimension the rail is short on to say less than it used to.
 *
 * A dropdown gives the palette dots a column instead of a row, and puts the
 * base case -- pick a look, done -- in the first control on the screen.
 *
 * NOT a native <select>: the dots are the reason the control works. A name
 * alone ("Government", "Modern") makes you apply a preset to find out what it
 * is, and applying is destructive -- a preset clears every key it omits, so
 * trying one on discards hand-tuning.
 *
 * `current` is DERIVED from the values by `matchPreset`, never remembered, so
 * the control cannot claim a look the preference no longer has. Null means
 * hand-tuned, and the button says so.
 */
export default function ThemePresetSelect ({ current, onApply }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    // Dismiss on outside click and Escape. Opening this to see what the
    // presets look like -- without applying one -- is the main reason to
    // open it, so there must be a way out that is not a selection.
    useEffect(() => {
        if (!open) return undefined;
        function onDown (ev) {
            if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
        }
        function onKey (ev) {
            if (ev.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const handlePick = useCallback((preset) => {
        onApply(preset);
        setOpen(false);
    }, [onApply]);

    const active = PRESETS.find(p => p.name === current) || null;

    return (
        <div className="theme-preset-select" ref={rootRef}>
            <span className="theme-preset-select__label" id="theme-preset-label">Preset</span>
            <button
                type="button"
                className="theme-preset-select__button"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-labelledby="theme-preset-label"
                onClick={() => setOpen(prev => !prev)}
            >
                <Dots preset={active}/>
                <span className="theme-preset-select__name">
                    {/* "Custom" rather than an empty control: a preference
                        that matches no preset is the normal end state of
                        using this screen, not a missing selection. */}
                    {active ? active.name : 'Custom'}
                </span>
                <i className="fa fa-caret-down theme-preset-select__caret" aria-hidden="true"/>
            </button>

            {open ? (
                <ul className="theme-preset-select__menu" role="listbox" aria-labelledby="theme-preset-label">
                    {PRESETS.map(preset => (
                        <li key={preset.name} role="option" aria-selected={preset.name === current}>
                            <button
                                type="button"
                                className={'theme-preset-select__option' +
                                    (preset.name === current ? ' is-active' : '')}
                                onClick={() => handlePick(preset)}
                            >
                                <Dots preset={preset}/>
                                <span className="theme-preset-select__option-text">
                                    <span className="theme-preset-select__option-name">{preset.name}</span>
                                    <span className="theme-preset-select__option-desc">{preset.description}</span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

/**
 * A preset's palette, as dots. Unset keys fall back to the display swatches,
 * so a preset that only sets an accent still reads as a complete look rather
 * than as four holes.
 */
function Dots ({ preset }) {
    return (
        <span className="theme-preset-select__dots" aria-hidden="true">
            {CHIP_KEYS.map(k => (
                <i
                    key={k}
                    style={{ background: (preset && preset.values[k]) || DEFAULT_SWATCHES[k] }}
                />
            ))}
        </span>
    );
}
